
import { useState, useEffect, useMemo } from 'react';
import { Trash, Edit, Plus } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { SwitchWithLabel } from '../../components/ui/switch-with-label';
import { storage } from '../../lib/storage';
import { Batch, Subject, ClassInfo } from '../../types/common';
import { useToast } from '../../hooks/use-toast';
import { getAuth } from 'firebase/auth';
import { collection, doc, FirestoreError, getDoc, getDocs, getFirestore, onSnapshot, setDoc } from 'firebase/firestore';
import { app } from "../../firebase";

import './Classes.css';

const Classes = () => {
  const { toast } = useToast();
  const [isActive, setIsActive] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [batches, setBatches] = useState<Batch[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classInfos, setClassInfos] = useState<ClassInfo[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingClassInfo, setEditingClassInfo] = useState<ClassInfo | null>(null);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [courseSubjects, setCourseSubjects] = useState([]);

  const [courseTodayClasses, setCourseTodayClasses] = useState([]);
  const [courses, setCourses] = useState([]);

  const db = getFirestore(app);
  const auth = getAuth(app);

  //courseTodayClasses
  useEffect(() => {

    const unsubscribe = onSnapshot(collection(db, 'courseTodayClasses'), (snapshot) => {
      const classesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCourseTodayClasses(classesData);
    }, (error) => {
      console.error("Error fetching data: ", error);
    });


    const coursesUnsubscribe = onSnapshot(collection(db, 'courses'), (snapshot) => {
      const coursesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCourses(coursesData);
    }, (error) => {
      console.error("Error fetching data: ", error);
    });


    const batchesUnsubscribe = onSnapshot(collection(db, 'courseBatches'), (snapshot) => {
      const batchesData: any = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })).sort(sortByBatchName);
      setBatches(batchesData);
    }, (error) => {
      console.error("Error fetching data: ", error);
    });

    const allSubjectsUnsubscribe = onSnapshot(collection(db, 'allSubjects'), (snapshot) => {
      const subjectsData: any = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSubjects(subjectsData);
    }, (error) => {
      console.error("Error fetching data: ", error);
    });


    const courseSubjectUnsubscribe = onSnapshot(collection(db, 'courseSubjects'), (snapshot) => {
      const subjectsData: any = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })).map((x: any) => {
        return {
          ...x,
          subjects: x.subjects?.map(x => x.subjectName)
        }
      });
      console.log(subjectsData);
      setCourseSubjects(subjectsData);
    }, (error) => {
      console.error("Error fetching data: ", error);
    });



    // Cleanup function to unsubscribe from the listener  
    return () => {
      unsubscribe();
      coursesUnsubscribe();
      batchesUnsubscribe();
      allSubjectsUnsubscribe();
      courseSubjectUnsubscribe();
    }
  }, [isActive]);

  const handleActiveToggle = (checked: boolean) => {
    setIsActive(checked);
    setSelectedBatch(null);

  };

  const handleAddClass = () => {
    const batch = batches.find(b => b.id === selectedBatch);
    if (batch) {
      setEditingClassInfo({
        id: "",
        batchId: "",
        batchName: batch?.batchName || "",
        courseName: courses?.find(x => x.id == batch?.courseId) || "",
        subjects: []
      });

    } else {
      setEditingClassInfo({
        id: "",
        batchId: "",
        batchName: "",
        courseName: "",
        subjects: []
      });
    }

    setSelectedSubjects([]);
    setIsAddDialogOpen(true);
  };

  const handleEditClass = (classInfo: ClassInfo) => {
    setEditingClassInfo(classInfo);
    setSelectedSubjects(Array.isArray(classInfo.subjects) ? classInfo.subjects : []);
    setIsAddDialogOpen(true);
  };

  const handleDeleteClass = (batchId: string) => {
    // Filter out the class info for this batch
    const updatedClassInfos = classInfos.filter(info => info.batchId !== batchId);
    setClassInfos(updatedClassInfos);

    // Update in storage
    toast({
      title: "Class Deleted",
      description: "The class has been removed successfully.",
      className: "bg-bytesplash-blue text-white",
    });
  };

  const handleSaveClass = async () => {
    if (!editingClassInfo || !editingClassInfo?.batchId) {
      toast({
        title: "Error",
        description: "Please select a batch.",
        variant: "destructive"
      });
      return;
    }

    const updatedClassInfo = {
      ...editingClassInfo,
      subjects: selectedSubjects || []
    };
    if (editingClassInfo?.id) {
      try {
        const courseRef = doc(db, "courseTodayClasses", editingClassInfo.id);
        await setDoc(courseRef, updatedClassInfo); // Update the course data in Firestore  

        // Update local state  
        if (!updatedClassInfo.id) {
          updatedClassInfo.id = courseRef.id; // Set the ID from Firestore  
          const newClassInfos = [...classInfos, updatedClassInfo];
          setClassInfos(newClassInfos);
        } else {
          const newClassInfos = classInfos.map(info =>
            info.id === updatedClassInfo.id ? updatedClassInfo : info
          );
          setClassInfos(newClassInfos);
        }

        // Close dialog  
        setIsAddDialogOpen(false);
        setEditingClassInfo(null);

        toast({
          title: updatedClassInfo.id ? "Class Updated" : "Class Added",
          description: `Class has been ${updatedClassInfo.id ? 'updated' : 'added'} successfully.`,
          className: "bg-bytesplash-blue text-white",
        });
      } catch (error) {
        console.error("Error updating class info: ", error);
        toast({
          title: "Error",
          description: "Failed to save class information.",
          variant: "destructive"
        });
      }
    } else {
      // Case for creating a new class  
      try {
        const newDocRef = doc(collection(db, "courseTodayClasses")); // Create a new document reference  
        await setDoc(newDocRef, { ...updatedClassInfo, id: newDocRef?.id }); // Add the new class info  

        // Update local state with new class info  
        updatedClassInfo.id = newDocRef.id; // Set the ID from Firestore  
        const newClassInfos = [...classInfos, updatedClassInfo];
        setClassInfos(newClassInfos);

        // Close dialog  
        setIsAddDialogOpen(false);
        setEditingClassInfo(null);

        toast({
          title: "Class Added",
          description: "New class has been added successfully.",
          className: "bg-bytesplash-blue text-white",
        });
      } catch (error) {
        console.error("Error adding new class info: ", error);
        toast({
          title: "Error",
          description: "Failed to add class information.",
          variant: "destructive"
        });
      }
    }
  };

  const handleSubjectToggle = (subject: string) => {
    if (Array.isArray(selectedSubjects)) {
      if (selectedSubjects.includes(subject)) {
        setSelectedSubjects(selectedSubjects.filter(s => s !== subject));
      } else {
        setSelectedSubjects([...selectedSubjects, subject]);
      }
    } else {
      setSelectedSubjects([subject]);
    }
  };

  const handleChangeSelectedBatch = (e) => {
    if (editingClassInfo) {
      setEditingClassInfo(prevState => ({
        ...prevState,
        isActive: !prevState.isActive,       // Toggle the isActive state                // Update the batchId with the selected one  
      }));
    }
  };

  // Safe filter for batches - ensure we're handling potential undefined values  
  const filteredBatches = useMemo(() => {
    return batches.filter(b =>
      typeof b === 'object' &&
      b !== null &&
      typeof b.isActive === 'boolean' &&
      b.isActive === isActive
    );
  }, [batches, isActive]); // Dependency array includes batches and isActive  

  const selectedBatches = useMemo(() => {
    if (!selectedBatch) {
      return JSON.parse(JSON.stringify(filteredBatches));
    }

    return JSON.parse(JSON.stringify(
      filteredBatches.filter((x) => x.id === selectedBatch)
    ));
  }, [filteredBatches, selectedBatch]); // Dependency array includes filteredBatches and selectedBatch  


  const findCourseName = (courseId: any) => {
    return courses?.find(x => x.id == courseId)?.courseName ?? '';
  }


  const sortByBatchName = (a: any, b: any) => {
    if (a.batchName < b.batchName) {
      return -1; // a should come before b  
    }
    if (a.batchName > b.batchName) {
      return 1; // a should come after b  
    }
    return 0; // a and b are equal  
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <p className="text-bytesplash-blue font-medium bg-bytesplash-soft-blue rounded-full px-4 py-1 inline-block">
            {new Date().toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </p>
          <h1 className="text-2xl font-bold text-bytesplash-blue mt-2">Today Classes</h1>
        </div>

        <div className="flex items-center gap-4">
          <Select
            value={selectedBatch}
            onValueChange={(val) => val && setSelectedBatch(val)}
          >
            <SelectTrigger className="w-[200px] border-2 hover:border-bytesplash-blue">
              <SelectValue placeholder="Select Batch" />
            </SelectTrigger>
            <SelectContent>
              {filteredBatches.map((batch) => (
                <SelectItem key={batch.id} value={batch.id}>
                  {batch?.batchName || "Unnamed Batch"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <SwitchWithLabel
            label="Active"
            checked={isActive}
            onCheckedChange={handleActiveToggle}
            activeColor="bg-bytesplash-blue"
            labelColor="text-bytesplash-blue font-medium"
            className="px-4 py-1 border rounded-full bg-white shadow-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          {selectedBatches?.map((batch) => {
            // Safely check if batch name exists
            const batchName = batch?.batchName || "Unnamed Batch";
            const classInfo = courseTodayClasses.find(c => c.batchId == batch?.id) ?? null;
            return (
              !!classInfo && (
                <Card key={batch.id} className="overflow-hidden shadow-md border-t-2 border-t-bytesplash-blue">
                  <div className="flex justify-between items-start p-4 bg-gray-50">
                    <div className="flex gap-2 items-center">
                      <div className="px-3 py-1 text-sm font-semibold bg-bytesplash-soft-blue text-bytesplash-blue rounded-md">
                        {batchName}
                      </div>
                      <h3 className="text-sm font-medium text-gray-600">
                        {findCourseName(batch?.courseId) || "No Course"}
                      </h3>
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        type='button'
                        className="h-8 w-8"
                        onClick={() => classInfo ? handleEditClass(classInfo) : handleAddClass()}
                      >
                        {classInfo ? <Edit className="h-4 w-4 text-bytesplash-blue" /> : <Plus className="h-4 w-4 text-green-600" />}
                      </Button>

                      {classInfo && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDeleteClass(batch.id)}
                        >
                          <Trash className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="mb-2">
                      <h4 className="text-sm font-medium text-gray-700">Today Classes</h4>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {classInfo && Array.isArray(classInfo.subjects) && classInfo.subjects.length > 0 ? (
                        classInfo.subjects?.map((subject, idx) => (
                          <div
                            key={idx}
                            className="px-3 py-1 text-sm bg-gray-100 text-gray-800 rounded-full"
                          >
                            {subject}
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">No classes assigned for today</p>
                      )}
                    </div>
                  </div>
                </Card>)
            );
          })}
        </div>

        <div className="flex justify-center">
          <Button
            className="flex items-center gap-2 bg-bytesplash-blue hover: bytesplash-dark-blue"
            onClick={() => handleAddClass()}
          >
            <Plus className="h-4 w-4" />
            Add New Batch Classes
          </Button>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[97vh] p-4 max-w-[70vw] min-w-[70vw]">
          <DialogHeader>
            <DialogTitle>
              {editingClassInfo?.id ? "Edit Classes" : "Add New Classes"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-2 py-2">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Batch</Label>
              <div className="col-span-3 p-2">
                <Select
                  value={editingClassInfo?.batchId || ""}
                  onValueChange={(value) => {
                    if (!value) return;
                  
                    const batch = batches.find(b => b.id === value);
                    console.log(batch);
                    if (batch) {
                      setEditingClassInfo(prev=>({
                        ...editingClassInfo!,
                        batchId: value,
                        batchName: batch?.batchName || "Unnamed Batch",
                        isActive: true,
                        courseId: batch?.courseId,
                        courseName: findCourseName(batch?.courseId) || "No Course"
                      }));
                    }
                  }}
                  disabled={!!editingClassInfo?.id}
                >
                  <SelectTrigger className="border-2 hover:border-bytesplash-blue">
                    <SelectValue placeholder="Select batch" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredBatches.map((batch) => (
                      <SelectItem key={batch.id} value={batch.id}>
                        {batch.batchName || "Unnamed Batch"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Status</Label>
              <div className="col-span-3 flex items-center">
                <SwitchWithLabel
                  label="Active"
                  checked={editingClassInfo?.isActive ?? true}
                  disabled={true}
                  onCheckedChange={(e) => { }}
                  activeColor="bg-bytesplash-blue"
                  labelColor="text-bytesplash-blue font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Subjects</Label>
              <div className="col-span-3 flex flex-wrap gap-4 mt-4">
                {courseSubjects.find(x => x.courseId === editingClassInfo?.courseId)?.subjects?.map((subject, i) => (
                  <div key={`${i}_${subject}`} className="flex items-center space-x-1 w-1/3 custom-checkbox">
                    <input
                      type="checkbox"
                      id={`subject-${i}`}
                      checked={Array.isArray(selectedSubjects) && selectedSubjects.includes(subject)}
                      onChange={() => handleSubjectToggle(subject)}
                      className="h-4 w-4 rounded border-gray-300 text-[#0071bd] focus:ring-0 peer"
                    />
                    <label
                      htmlFor={`subject-${i}`}
                      className="text-sm font-medium leading-none cursor-pointer peer-checked:text-[#0071bd]"
                    >
                      {subject || "Unnamed Subject"}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="sm:justify-between mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsAddDialogOpen(false);
                setEditingClassInfo(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveClass}
              disabled={!Array.isArray(selectedSubjects) || selectedSubjects.length === 0}
              className="bg-bytesplash-blue hover:bg-blue-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Classes;
